import React, {  } from 'react';
import styled from 'styled-components/macro';

import { border, colors, distances } from '../../../styles/constants';
import { InputValidation } from '../../../components/Forms/validation';
import { TextareaElement } from '../../../components/Forms/TextAreaElement';

export type OnChangeFunction = (event: Event) => void;

interface InputProps {
    name: string;
    label: any;
    placeholder: string;
    value: string;
    onBlur: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    onChange: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    disabled?: boolean;
    type?: string;
    validation?: InputValidation;
    rightAligned?: boolean;
    prefix?: string;
    autoComplete?: string;
    maxlength?: number;
    className?: string;
    autoFocus?: boolean;
    labelOpacity?: number;
    isTextArea?: boolean;
    paddingLeft?: string;
    [key: string]: any;
}

const MarketingInput = (props: InputProps) => {
    let input: any = null;

    const whitelistedHtmlProps = ['aria-autocomplete', 'aria-activedescendant', 'aria-controls', 'aria-labelledby'];

    interface AnyObject {
        [key: string]: any;
    }

    const otherProps = Object.keys(props)
        .filter((key) => whitelistedHtmlProps.includes(key))
        .reduce((obj: AnyObject, key: string) => {
            obj[key] = props[key];
            return obj;
        }, {});

    return (
        <Wrapper className={props.className}>
            <Label htmlFor={props.name} opacity={props.labelOpacity}>
                {props.label}
            </Label>
            {!!props.isTextArea ? (
                <TextareaElement
                    {...otherProps}
                    autoComplete={props.autoComplete}
                    autoFocus={props.autoFocus}
                    rightAligned={props.rightAligned || false}
                    value={props.value}
                    id={props.name}
                    name={props.name}
                    disabled={props.disabled}
                    placeholder={props.placeholder}
                    ref={(elem) => {
                        input = elem;
                    }}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    onKeyDown={props.onKeyDown}
                    className={props.validation ? props.validation.state : ''}
                    maxLength={props.maxlength}
                    prefix={props.prefix}
                />
            ) : (
                <InputElement
                    {...otherProps}
                    autoComplete={props.autoComplete}
                    autoFocus={props.autoFocus}
                    rightAligned={props.rightAligned || false}
                    value={props.value}
                    id={props.name}
                    type={props.type || 'text'}
                    name={props.name}
                    disabled={props.disabled}
                    placeholder={props.placeholder}
                    ref={(elem) => {
                        input = elem;
                    }}
                    onChange={props.onChange}
                    onBlur={props.onBlur}
                    onKeyDown={props.onKeyDown}
                    className={props.validation ? props.validation.state : ''}
                    maxLength={props.maxlength}
                    prefix={props.prefix}
                    paddingLeft={props.paddingLeft}
                />
            )}
            {props.prefix && (
                <Prefix
                    onClick={() => {
                        input && input.focus();
                    }}
                >
                    {props.prefix}
                </Prefix>
            )}
            {props.validation && props.validation.message && (
                <Message htmlFor={props.name} className={props.validation.state} isTextArea={!!props.isTextArea}>
                    {props.validation.message}
                </Message>
            )}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    position: relative;
`;

const Label = styled.label<{ opacity?: number }>`
    padding: ${distances.tiny} 0;
    font-weight: 700;
    display: block;
    opacity: ${(props) => (props.opacity ? props.opacity : 1)};
    white-space: nowrap;
`;

const Prefix = styled.label`
    position: absolute;
    top: 40px;
    left: ${distances.small};
    color: ${colors.textSecondary};
    cursor: text;
`;

interface InputElementProps {
    rightAligned: boolean;
    prefix?: string;
    paddingLeft?: string;
}

const InputElement = styled.input<InputElementProps>`
    min-height: 40px;
    border: ${border.normal} solid ${colors.borderDark};
    border-radius: 2px;
    outline: none;
    background: ${colors.background};
    padding: ${distances.tiny} ${distances.small};
    text-align: ${(props) => (props.rightAligned ? 'right' : 'left')};
    width: 100%;
    -moz-appearance: textfield;
    margin-bottom: ${distances.small};

    ${(props) => (props.prefix ? `padding-left: calc(${distances.small} + ${props.prefix.length * 12}px);` : '')}
    ${(props) => (props.paddingLeft ? `padding-left: ${props.paddingLeft}px` : '')}


    &::-webkit-inner-spin-button {
        display: none;
        -webkit-appearance: none;
    }

    &::placeholder {
        color: ${colors.textSecondary};
    }

    &:focus {
        outline: none;
        background: ${colors.interactiveBackgroundHover};
    }
    &.invalid,
    &:focus .invalid {
        border-bottom: ${border.normal} solid ${colors.invalid};
    }

    &.valid,
    &:focus .valid {
        border-bottom: ${border.normal} solid ${colors.valid};
    }

    &:disabled {
        background: ${colors.backgroundAlt};
    }
`;

const Message = styled.label<{ isTextArea: boolean }>`
    position: absolute;
    top: ${(props) => (props.isTextArea ? '160px' : '70px')};
    left: 0;
    font-size: 11px;
    line-height: ${distances.small};

    &.invalid {
        color: ${colors.invalid};
    }

    &.valid {
        color: ${colors.valid};
    }
`;

export default MarketingInput;
